import { useEffect, RefObject } from 'react'

import { FOCUSABLE_SELECTOR } from '@/lib/focusable-selector'

/**
 * Hook to trap focus within a container element.
 * Useful for modals and dialogs to ensure keyboard navigation stays within.
 *
 * @param containerRef - Ref to the container element
 * @param enabled - Whether the focus trap is active
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current

    // Find all focusable elements. The selector is shared with
    // `ConfirmationDialog`, which asks the same question of a dialog's body
    // before its cancel-focus timer fires — see `@/lib/focusable-selector`
    // for why the two must not drift (#13990).
    const getFocusableElements = () => {
      return Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(el => el.offsetParent !== null) // Filter out hidden elements
    }

    // Focus first element on mount
    const focusableElements = getFocusableElements()
    if (focusableElements.length > 0) {
      // Focus the first non-close-button element, or the first element
      const firstElement =
        focusableElements.find(
          el => !el.getAttribute('aria-label')?.includes('Close'),
        ) || focusableElements[0]
      firstElement?.focus()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusable = getFocusableElements()
      if (focusable.length === 0) return

      const firstElement = focusable[0]
      const lastElement = focusable[focusable.length - 1]

      // Shift+Tab on first element -> go to last
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement?.focus()
      }
      // Tab on last element -> go to first
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement?.focus()
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [containerRef, enabled])
}
